import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import useUserStore from "@store/useUserStore";
import { TokenManager } from "utils/api";
import BottomNavigation from "components/BottomNavigation";
import { MessageListEmptyState, MessageListRow } from "./MessageComponents";
import { formatRelativeTime, listMessageRooms, pickMessageTimestamp } from "api/chat";
import { createChatSocket } from "api/chatSocket";
import { readCachedRooms, writeCachedRoom, writeCachedRooms } from "./roomCache";
import useRefreshCallback from "hooks/useRefreshCallback";
import "./Messages.scss";
import SubHeader from "../../components/common/SubHeader";

const getCurrentUserId = (userInfo) => String(userInfo?.user_id ?? "");
const getMessageRoomId = (message = {}) => String(message.roomId ?? message.room_id ?? "");
const getMessageSenderId = (message = {}) => String(message.senderUserId ?? message.sender_user_id ?? "");
const getMessagePreview = (message = {}, fallback = "아직 메시지가 없습니다.") => {
    const body = String(message.body ?? "").trim();
    if (body) return body;
    if ((message.attachments || message.attachment_list || []).length > 0) return "첨부파일을 보냈습니다.";
    return fallback;
};
const joinMessageRooms = (socket, rooms = []) => {
    if (!socket?.emit) return;
    rooms.forEach((room) => {
        if (room?.id) socket.emit("room:join", { roomId: room.id });
    });
};
const mergeFetchedRoomsWithRealtime = (fetchedRooms = [], currentRooms = []) => {
    const currentIds = new Set(currentRooms.map((room) => String(room.id)));
    const fetchedOnly = fetchedRooms.filter((room) => !currentIds.has(String(room.id)));
    return [...currentRooms, ...fetchedOnly];
};

function MessageList() {
    const navigate = useNavigate();
    const userInfo = useUserStore((state) => state.userInfo);
    const hasAccessToken = Boolean(TokenManager.getAccessToken());
    const [rooms, setRooms] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const roomsRef = useRef(rooms);
    const socketRef = useRef(null);
    const visibleRequestIdRef = useRef(0);
    const silentRequestIdRef = useRef(0);
    const realtimeVersionRef = useRef(0);
    const isMountedRef = useRef(true);
    const currentUserId = getCurrentUserId(userInfo);

    const loadRooms = useCallback(({ silent = false } = {}) => {
        if (!currentUserId || !hasAccessToken) return Promise.resolve();
        const requestId = (silent ? silentRequestIdRef : visibleRequestIdRef).current + 1;
        if (silent) {
            silentRequestIdRef.current = requestId;
        } else {
            visibleRequestIdRef.current = requestId;
        }
        const realtimeVersionAtStart = realtimeVersionRef.current;
        if (!silent && roomsRef.current.length === 0) {
            setIsLoading(true);
            setErrorMessage("");
        }

        return listMessageRooms()
            .then((nextRooms) => {
                if (!isMountedRef.current) return;
                if (silent && requestId !== silentRequestIdRef.current) return;
                if (!silent && requestId !== visibleRequestIdRef.current) return;
                const shouldPreserveRealtime = realtimeVersionRef.current !== realtimeVersionAtStart;
                if (!silent && shouldPreserveRealtime && roomsRef.current.length > 0) return;
                const resolvedRooms = shouldPreserveRealtime
                    ? mergeFetchedRoomsWithRealtime(nextRooms, roomsRef.current)
                    : nextRooms;
                roomsRef.current = resolvedRooms;
                setRooms(resolvedRooms);
                writeCachedRooms(currentUserId, resolvedRooms);
                joinMessageRooms(socketRef.current, resolvedRooms);
            })
            .catch(() => {
                if (!silent && isMountedRef.current && requestId === visibleRequestIdRef.current) {
                    roomsRef.current = [];
                    setRooms([]);
                    setErrorMessage("메시지 목록을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.");
                }
            })
            .finally(() => {
                if (!silent && isMountedRef.current) setIsLoading(false);
            });
    }, [currentUserId, hasAccessToken]);

    useEffect(() => {
        isMountedRef.current = true;
        const cachedRooms = readCachedRooms(currentUserId);
        roomsRef.current = cachedRooms;
        setRooms(cachedRooms);
        loadRooms();
        return () => {
            isMountedRef.current = false;
        };
    }, [currentUserId, loadRooms]);

    useEffect(() => {
        joinMessageRooms(socketRef.current, rooms);
    }, [rooms]);

    // Pull-to-Refresh: 채팅방 목록 재조회
    useRefreshCallback(() => loadRooms());

    useEffect(() => {
        if (!currentUserId || !hasAccessToken) return undefined;
        const socket = createChatSocket();
        if (!socket) return undefined;
        socketRef.current = socket;

        const handleCreated = (message) => {
            const roomId = getMessageRoomId(message);
            if (!roomId) return;

            const currentRooms = roomsRef.current || [];
            const targetIndex = currentRooms.findIndex((room) => String(room.id) === roomId);
            if (targetIndex < 0) {
                realtimeVersionRef.current += 1;
                loadRooms({ silent: true });
                return;
            }

            const senderId = getMessageSenderId(message);
            const isMine = senderId && senderId === currentUserId;
            const targetRoom = currentRooms[targetIndex];
            const updatedRoom = {
                ...targetRoom,
                lastMessage: getMessagePreview(message, targetRoom.lastMessage),
                lastMessageAt: formatRelativeTime(pickMessageTimestamp(message)) || targetRoom.lastMessageAt,
                unread: isMine ? Number(targetRoom.unread || 0) : Number(targetRoom.unread || 0) + 1,
            };
            const nextRooms = [updatedRoom, ...currentRooms.filter((_, index) => index !== targetIndex)];
            realtimeVersionRef.current += 1;
            roomsRef.current = nextRooms;
            setRooms(nextRooms);
            writeCachedRooms(currentUserId, nextRooms);
        };

        const handleConnect = () => joinMessageRooms(socket, roomsRef.current);

        socket.on("connect", handleConnect);
        socket.on("message:created", handleCreated);
        socket.on("connect_error", () => undefined);
        if (socket.connected) handleConnect();

        return () => {
            socket.off("connect", handleConnect);
            socket.off("message:created", handleCreated);
            socket.disconnect();
            if (socketRef.current === socket) socketRef.current = null;
        };
    }, [currentUserId, hasAccessToken, loadRooms]);

    if (!userInfo || !hasAccessToken) {
        return <Navigate to="/login" replace />;
    }

    return (
        <main className="messages-page messages-page--list">
            <section className="message-list-panel" aria-labelledby="message-list-title">
                {/* <header className="message-list-header">
                    <div>
                        <h1 id="message-list-title">메시지</h1>
                    </div>
                </header> */}
                <SubHeader title="메시지" />

                {isLoading && <p className="message-state">메시지를 불러오는 중입니다.</p>}
                {errorMessage && !isLoading && <p className="message-state message-state--error">{errorMessage}</p>}
                {!isLoading && !errorMessage && rooms.length === 0 && <MessageListEmptyState />}

                <div className="message-list" role="list">
                    {rooms.map((room) => (
                        <MessageListRow
                            key={room.id}
                            room={room}
                            onClick={() => {
                                writeCachedRoom(currentUserId, room);
                                navigate(`/messages/${room.id}`, { state: { room, cacheUserId: currentUserId } });
                            }}
                        />
                    ))}
                </div>
            </section>
            <BottomNavigation />
        </main>
    );
}

export default MessageList;
